import React, { Suspense, useState, useMemo } from 'react';
import { ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import 'react-toastify/dist/ReactToastify.css';

import styles from './style/PortfolioDetail.module.css';
import CommonStyles from './../../../styles/Common.module.css';

import { usePortfolioDetail } from './hooks/usePortfolioDetail';
import PortfolioHeader from './partials/Detail-Header';
import PortfolioCharts from './partials/Detail-TimeSeriesCharts'; 
import PortfolioRadarChart from './partials/Detail-RadarChart'; 
import PortfolioDetailAttribution from './partials/Detail-Attribution'; 
import PortfolioCurrentComposition from './partials/Detail-CurrentComposition'; 
import PortfolioCompositionHistory from './partials/Detail-CompositionHistory'; 

import NoDataView from './partials/Detail-NoDataView';
import RebalanceModal from './modal/RebalanceModal';
import CommentSection from '../../Comment/CommentSection';

const Portfolios = React.lazy(() => import('./Portfolios'));

// 탭 이름 상수
const TABS = {
    COMPOSITION_ANALYSIS: 'compositionAnalysis', // 비중 분석 (현재 비중 + 비중 추이)
    PERFORMANCE_CHARTS: 'performanceCharts',   // 성과 추이 (수익률/낙폭)
};

// ✨ 추가: 기간 상수 정의 (데이터 필드 접미사와 일치해야 함)
const TIME_PERIODS = {
    '1W': '1w',
    '2W': '2w',
    '1M': '1m',
    '3M': '3m',
    '6M': '6m',
    '1Y': '1y',
    // '2Y': '2y',
    // '3Y': '3y',
    // 'ALL': 'all',
};
export default function PortfolioDetail() {
  const { t } = useTranslation();
  const auth = useAuth();

  const {
    portfolioId, po, timeSeries, err, loading, isNoData,
    isFollowing, showRebalanceModal, setShowRebalanceModal,
    handleFollowToggle, handleRebalance,
    currentUserId, navigate, DEFAULT_PROFILE_URL, API_BASE_URL
  } = usePortfolioDetail(t);

  // 탭 초기 상태
  const [showCalculationExplanation, setShowCalculationExplanation] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.COMPOSITION_ANALYSIS);
  // ✨ 추가: 현재 활성화된 기간 상태 (기본값 'all')
  const [activePeriod, setActivePeriod] = useState(TIME_PERIODS.ALL);

  // ✨ 수정: summaryMetrics 계산 로직을 activePeriod에 따라 동적으로 변경
  const summaryMetrics = useMemo(() => {
    if (isNoData || !po) return null;

    // 선택된 기간의 접미사 결정 (예: 'all', '1m', '3m')
    const periodSuffix = activePeriod === TIME_PERIODS.ALL ? '_all' : `_${activePeriod}`;
    
    // 동적으로 키 이름 생성
    const totalReturnKey = `ret${periodSuffix}`;
    const mddKey = `mdd${periodSuffix}`;
    const sharpeKey = `sharpe${periodSuffix}`;
    const volatilityKey = `vol${periodSuffix}`;
    
    // po 객체에서 해당 기간의 데이터 추출
    const totalReturn = (po[totalReturnKey] ?? 0) * 100;
    const mdd = (po[mddKey] ?? 0) * 100;
    const sharpe = po[sharpeKey] ?? 0;
    const volatility = (po[volatilityKey] ?? 0) * 100;

    // console.log(`Metrics for ${activePeriod}:`, { totalReturn, mdd, sharpe, volatility });

    return { totalReturn, mdd, sharpe, volatility };
  }, [po, isNoData, activePeriod]); // ✨ 의존성 배열에 activePeriod 추가

  // Early returns must come after all hooks are called
  if (loading) return <div className={CommonStyles.loadingContainer}><div className={CommonStyles.spinner}></div><p className={CommonStyles.message}>{t('portfolioDetail.loading')}</p></div>;
  if (err) return <p className={`${styles.message} ${styles.error}`}>{t('portfolioDetail.error')}{err}</p>;
  if (!po) return null;

  const isOwner = auth.user?.profile?.sub === po.userId;

  return (
    <>
      <div className={styles.container}>
        {/* 1. 헤더 섹션 (변동 없음) */}
        <PortfolioHeader
          po={po}
          isFollowing={isFollowing}
          handleFollowToggle={handleFollowToggle}
          currentUserId={currentUserId}
          navigate={navigate}
          t={t}
          DEFAULT_PROFILE_URL={DEFAULT_PROFILE_URL}
        />

        {/* 2. 요약 섹션 - Summary Metrics */}
        {summaryMetrics && (
          <div className={styles.summarySection}>
            <h3 className={styles.subTitle}>{t('portfolioDetail.summaryTitle')}</h3>
            
            {/* ✨ 추가: 기간 선택 버튼 그룹 */}
            <div className={styles.periodSelectors}>
              {Object.entries(TIME_PERIODS).map(([label, periodKey]) => (
                <button
                  key={periodKey}
                  className={`${styles.periodButton} ${activePeriod === periodKey ? styles.active : ''}`}
                  onClick={() => setActivePeriod(periodKey)}
                >
                  {label === 'ALL' ? t('common.all') : label}
                </button>
              ))}
            </div>
            
            <p className={styles.summaryInfoText}>
              {t('portfolioDetail.elapsedDaysInfo', { days: po.elapsedDays })}
            </p>
            <div className={styles.metricCards}>
              {/* ... (Metric Card 4개 렌더링) ... */}
              <div className={styles.card}>
                <span>{t('portfolioDetail.totalReturn')}</span>
                <span 
                  className={styles.value} 
                  style={{ color: summaryMetrics.totalReturn >= 0 ? '#4caf50' : '#ff4d4d' }}
                >
                  {summaryMetrics.totalReturn.toFixed(2)}%
                </span>
              </div>
              <div className={styles.card}>
                <span>{t('portfolioDetail.mdd')}</span>
                <span 
                  className={styles.value} 
                  style={{ color: summaryMetrics.mdd <= 0 ? '#ff4d4d' : '#4caf50' }}
                >
                  {summaryMetrics.mdd.toFixed(2)}%
                </span>
              </div>
              <div className={styles.card}>
                <span>{t('portfolioDetail.sharpe')}</span>
                <span 
                  className={styles.value} 
                  style={{ color: summaryMetrics.sharpe >= 1 ? '#4caf50' : '#ff9800' }}
                >
                  {summaryMetrics.sharpe.toFixed(2)}
                </span>
              </div>
              <div className={styles.card}>
                <span>{t('portfolioDetail.volatility')}</span>
                <span 
                  className={styles.value} 
                  style={{ color: summaryMetrics.volatility <= 15 ? '#4caf50' : '#ff9800' }}
                >
                  {summaryMetrics.volatility.toFixed(2)}%
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* 3. 핵심 분석 1: PortfolioRadarChart (리스크/스타일 분석) - 상시 노출 */}
        {po && !isNoData && (
          <>
            <PortfolioRadarChart po={po} t={t} />
          </>
        )}

        {/* 4. 핵심 분석 2: PortfolioDetailAttribution (수익 기여도 분석) - 상시 노출로 변경 */}
        {po && !isNoData && timeSeries?.stockAttribution && (
          <>
            <PortfolioDetailAttribution timeSeries={timeSeries} t={t} />
          </>
        )}

        {/* 5. 상세 분석 탭 섹션 */}
        {isNoData ? (
          <NoDataView t={t} />
        ) : (
          <div className={styles.tabSection}>
            <div className={styles.tabButtons}>
              <button
                className={`${styles.tabButton} ${activeTab === TABS.COMPOSITION_ANALYSIS ? styles.active : ''}`}
                onClick={() => setActiveTab(TABS.COMPOSITION_ANALYSIS)}
              >
                {t('portfolioDetail.tab.Holdings')} {/* 성과 추이 */}
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === TABS.PERFORMANCE_CHARTS ? styles.active : ''}`}
                onClick={() => setActiveTab(TABS.PERFORMANCE_CHARTS)}
              >
                {t('portfolioDetail.tab.PerformanceTrend')} {/* 비중 분석 */}
              </button>
            </div>

            <div className={styles.tabContent}>
              {/* 탭 1: 비중 분석 (현재 비중 + 비중 추이 통합) */}
              {activeTab === TABS.COMPOSITION_ANALYSIS && (
                <>
                  <PortfolioCurrentComposition timeSeries={timeSeries} t={t} />
                  <PortfolioCompositionHistory timeSeries={timeSeries} t={t} />
                </>
              )}
              
              {/* 탭 2: 성과 추이 (수익률/낙폭) */}
              {activeTab === TABS.PERFORMANCE_CHARTS && (
                <PortfolioCharts timeSeries={timeSeries} t={t} />
              )}
              
            </div>
          </div>
        )}
        
         {/* 6. 계산 설명 토글 (위치 유지) */}
        {po && !isNoData && (
          <div className={styles.toggleSection}>
            <button
              onClick={() => setShowCalculationExplanation(!showCalculationExplanation)}
              className={styles.infoButton}
            >
              <span>{t('portfolioDetail.calculationExplanationTitle')}</span>
              <span className={`${styles.infoIconToggle} ${showCalculationExplanation ? styles.open : ''}`}>▼</span>
            </button>
            {showCalculationExplanation && (
              <div className={styles.explanationText}>
                <p>{t('portfolioDetail.calculationExplanationText.p1')}</p>
                <p>{t('portfolioDetail.calculationExplanationText.p2')}</p>
                <p>{t('portfolioDetail.calculationExplanationText.p3')}</p>
              </div>
            )}
          </div>
        )}

        {/* 7. 댓글 섹션 및 모달 (변동 없음) */}
        {/* {portfolioId && (
          <CommentSection entityId={portfolioId} API_BASE_URL={API_BASE_URL} />
        )} */}

        {showRebalanceModal && isOwner && (
          <RebalanceModal
            portfolioId={portfolioId}
            onClose={() => setShowRebalanceModal(false)}
            onRebalanced={handleRebalance}
          />
        )}
      </div>

      <Suspense fallback={<p className={styles.message}>{t('portfolioDetail.loading')}</p>}>
        <Portfolios />
      </Suspense>

      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={true} />
    </>
  );
}